import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BotIcon, ClockIcon, FileSearchIcon } from 'lucide-react';
import { useAssessment } from '../contexts/AssessmentContext';
import { EVIDENCE_KEY } from '../utils/scoring';
import { EvidenceCategory } from '../types/assessment';
const STAGES = [{
  label: 'Bundle review against instructions',
  timing: '~25 min'
}, {
  label: 'Documents & assistant',
  timing: '~20 min'
}, {
  label: 'Submission & reflection',
  timing: '~15 min'
}];
const RULES = [{
  icon: ClockIcon,
  title: '60 minutes, one sitting',
  body: 'The timer starts when you begin and does not pause. You are not expected to review everything — choosing what to check is part of the work.'
}, {
  icon: BotIcon,
  title: 'Use the AI assistant freely',
  body: 'There is no penalty for using it and no bonus either. It has strong knowledge of corporate practice but cannot see the bundle — it knows only what you paste or explicitly share with it.'
}, {
  icon: FileSearchIcon,
  title: 'We measure how you verify',
  body: 'What you checked, against what, and what you took on trust. Polish, formatting, writing quality and speed are not assessed.'
}];
const KEY_ORDER: EvidenceCategory[] = ['resolved', 'clean-verified', 'escalated', 'over-or-incorrect', 'missed'];
export function BriefingLaw() {
  const {
    startAssessment,
    startedAt
  } = useAssessment();
  const navigate = useNavigate();
  const begin = () => {
    startAssessment();
    navigate('/workspace');
  };
  return <main className="w-full flex-1 bg-slate-100 pb-16" data-id="element-502" data-mp="2davlc82xvguh">
      <section className="bg-slate-900" data-id="element-503" data-mp="2i4zwl3j8mb1d">
        <div className="mx-auto max-w-[1100px] px-6 py-14" data-id="element-504" data-mp="2i6ax8uhdz2o9">
          <span className="inline-flex items-center rounded-full border border-slate-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-teal-400" data-id="element-505" data-mp="2n1q9jhzqwqik">
            Trainee Solicitor · Practical assessment
          </span>
          <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-tight tracking-tight text-white" data-id="element-506" data-mp="2w1dbc4wdzyc5">
            Project Harrow — corporate document review before completion
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-300" data-id="element-507" data-mp="rxs903z70whb">
            You are a first-seat trainee in the corporate team. The supervising associate has asked
            you to review the Project Harrow transaction bundle against the deal instructions before
            completion tomorrow and flag anything that requires attention.
          </p>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-300" data-id="element-508" data-mp="1apurzlzb4ouq">
            Review the documents, use the AI assistant as much as you find useful, and decide what
            should be raised with the supervising associate.
          </p>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-300" data-id="element-509" data-mp="3qh8zbux6uc8d">
            If an item is supported by the documents, record that and move on. If something does not
            reconcile, investigate it and decide whether it needs to be escalated.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1100px] px-6" data-id="element-510" data-mp="3gdttd2oznbkp">
        <div className="-mt-8 grid gap-4 sm:grid-cols-3" data-id="element-511" data-mp="3gfodiesfqznq">
          {STAGES.map(s => <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-512" data-mp="32lx7hgdwxxwr">
              <p className="font-mono-num text-xl font-semibold text-slate-900" data-id="element-513" data-mp="37j5p9fv5fzxh">{s.timing}</p>
              <p className="mt-1 text-sm font-medium text-slate-700" data-id="element-514" data-mp="2smoeapqvgnbg">{s.label}</p>
              <p className="mt-2 text-xs text-slate-500" data-id="element-515" data-mp="2xoniu2yclq9z">
                Indicative only — spend your time as you judge best.
              </p>
            </div>)}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-516" data-mp="2onfnntr4p5k4">
          {RULES.map(r => <div key={r.title} className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-517" data-mp="hwznud2wa7tt">
              <r.icon className="h-5 w-5 text-slate-900" data-id="element-518" data-mp="mvaz8s8u8qe5" />
              <h2 className="mt-3 text-[15px] font-semibold text-slate-900" data-id="element-519" data-mp="msopclyh5ysr">{r.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-520" data-mp="2iw35zmyt2i7f">{r.body}</p>
            </div>)}
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-id="element-521" data-mp="28zq5o68r963p">
          <h2 className="text-[15px] font-semibold text-slate-900" data-id="element-522" data-mp="291uijdomtfg4">The matter</h2>
          <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-523" data-mp="24y4s0ioecnsg">
            {[['Buyer', 'Ashworth Capital Ltd'], ['Target', 'Brindle Systems Ltd'], ['Seller named in the SPA', 'Vale Nominees Ltd'], ['Purchase price', '£4,200,000']].map(([label, value]) => <div key={label} data-id="element-524" data-mp="1lap3h0qimr6v">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-525" data-mp="1lbqej57j7dol">
                  {label}
                </dt>
                <dd className="mt-0.5 text-sm text-slate-900" data-id="element-526" data-mp="1hamyoiphhhnd">{value}</dd>
              </div>)}
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-slate-600" data-id="element-527" data-mp="3nxcgoxioc4r6">
            This is not a test of specialist legal knowledge. Everything you need is in the bundle,
            and every question can be settled by reading the documents carefully. You are a trainee:
            you do not have authority to conclude the transaction, and recognising where a decision
            belongs to the supervising associate is part of what is being assessed.
          </p>
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-id="element-528" data-mp="3986b8rt8tiau">
          <h2 className="text-[15px] font-semibold text-slate-900" data-id="element-529" data-mp="3963oqhrau7av">How this is read</h2>
          <p className="mt-1.5 text-sm text-slate-600" data-id="element-530" data-mp="3e5ycwdjowis8">
            Each item in the bundle is read against one of these descriptions. There is no score and
            no ranking.
          </p>
          <ul className="mt-4 space-y-2" data-id="element-531" data-mp="2li2hdc5uwwx1">
            {KEY_ORDER.map(k => <li key={k} className={`rounded-lg border px-4 py-2.5 text-[13.5px] font-medium ${EVIDENCE_KEY[k].className}`} data-id="element-532" data-mp="2qgdubvrg7njh">
                {EVIDENCE_KEY[k].label}
              </li>)}
          </ul>
          <p className="mt-4 text-sm text-slate-600" data-id="element-533" data-mp="6fa78mezvom2">
            Polish, formatting, writing quality and speed are not assessed.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-534" data-mp="2eq9phqieize">
          <p className="max-w-md text-sm text-slate-600" data-id="element-535" data-mp="35ux0inntslqr">
            The clock starts the moment you press this and does not pause. Your work saves
            automatically; if you lose connection, reopen this page and you will resume where you
            left off.
          </p>
          <button type="button" onClick={begin} className="group inline-flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-slate-700" data-id="element-536" data-mp="3exofgxfwtzn5">
            {startedAt ? 'Return to assessment' : 'Begin assessment'}
            <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-537" data-mp="16o86pxyoa2xy" />
          </button>
        </div>
      </div>
    </main>;
}